export interface Article {
  slug: string;
  title: string;
  excerpt: string;
  category: string;
  imageSeq: string;
  imagePrompt: string;
  date: string;
  readTime: string;
  author: string;
  contentHtml: string;
  tags: string[];
  featured: boolean;
}

export const articles: Article[] = [
  {
    slug: 'goblin-cave-beginner-complete-guide',
    title: '哥布林的洞窟新手完全入门指南：从零开始的冒险之旅',
    excerpt: '刚踏入哥布林的洞窟不知道从何下手？本篇为你梳理最完整的新手入门路线，涵盖角色创建、基础操作、初期资源获取与生存技巧，让你在黑暗洞穴中快速站稳脚跟。',
    category: 'beginner-guide',
    imageSeq: 'article-beginner-01',
    imagePrompt: 'First person view entering a dark fantasy dungeon entrance, torch in hand illuminating rough stone walls covered in moss, ancient wooden door slightly ajar with warm light spilling through, cinematic game environment concept art, rich atmospheric lighting with amber and dark brown tones',
    date: '2026-07-15',
    readTime: '12分钟',
    author: '洞窟探险家',
    tags: ['新手入门', '基础操作', '生存指南'],
    featured: true,
    contentHtml: `
      <h2>欢迎来到哥布林的洞窟</h2>
      <p>当你第一次踏入<strong>哥布林的洞窟</strong>，扑面而来的潮湿空气和远处若隐若现的火光会让你瞬间感受到这个地下世界的压迫感。别担心，每一位伟大的冒险者都曾经是站在洞口手足无措的新人。本篇指南将手把手带你走过最初的适应期。</p>

      <h3>第一步：角色创建与职业选择</h3>
      <p>进入游戏后，你首先需要创建一个冒险者角色。哥布林的洞窟目前提供<strong>四种初始职业</strong>：</p>
      <ul>
        <li><strong>战士</strong>——高生命值和防御力，适合新手。初始武器为铁剑和木盾，技能偏向近战格挡与反击。</li>
        <li><strong>游侠</strong>——远程输出职业，使用弓箭和陷阱。擅长在安全距离消耗敌人，但防御较低需要走位技巧。</li>
        <li><strong>法师</strong>——魔法伤害爆炸但极其脆皮。初期拥有火球术和魔法盾两个技能，适合有RPG经验的玩家。</li>
        <li><strong>盗贼</strong>——高暴击高闪避的刺客型职业。初始掌握背刺和隐身，适合喜欢高风险高回报玩法的玩家。</li>
      </ul>
      <p><strong>建议新手选择战士开局</strong>。战士的容错率最高，即使操作失误也不至于被秒杀，能让你有更多时间熟悉怪物攻击模式。</p>

      <h3>第二步：熟悉基础操作</h3>
      <p>哥布林的洞窟的基础操作并不复杂，但有一些细节值得注意：</p>
      <ul>
        <li><strong>移动与跳跃</strong>：WASD移动，空格键跳跃。洞窟中很多隐藏区域需要二段跳才能到达，建议在设置中开启"自动二段跳"选项。</li>
        <li><strong>攻击与防御</strong>：鼠标左键普通攻击，右键格挡/瞄准。按住Shift键可以锁定目标，这对近战职业尤其重要。</li>
        <li><strong>交互与拾取</strong>：按E键与环境互动。发光物体通常都是可拾取道具，靠近按E即可收入背包。</li>
        <li><strong>快捷栏使用</strong>：数字键1-8对应快捷栏道具。建议将生命药水固定在1号位，法力药水在2号位。</li>
      </ul>

      <h3>第三步：初期资源收集路线</h3>
      <p>进入第一层"废弃矿道"后，不要急于深入。先在入口区域刷几波基础怪物积累经验和资源：</p>
      <ul>
        <li><strong>矿道蝙蝠</strong>（1-3级）：掉落蝙蝠翅膀和少量金币，是初期练手的最佳对象。</li>
        <li><strong>洞穴史莱姆</strong>（2-4级）：掉落史莱姆凝胶，可用于制作基础药水。注意绿色史莱姆带有毒性攻击。</li>
        <li><strong>矿工骷髅</strong>（3-5级）：掉落铁矿石和破损装备，可分解为锻造材料。</li>
      </ul>
      <p>建议在入口区域刷到5级后再继续前进。记得在篝火处休息可以回复全部状态并保存进度。</p>

      <h3>第四步：第一个BOSS——矿道守卫者</h3>
      <p>当你到达矿道尽头时，会遭遇第一个BOSS<strong>矿道守卫者</strong>。这个巨大的石魔像看似可怕，但掌握规律后并不难对付：</p>
      <ul>
        <li><strong>攻击模式一</strong>：地面锤击，有明显的前摇动作，看到它举起双臂立即向侧面翻滚躲避。</li>
        <li><strong>攻击模式二</strong>：岩石投掷，远程攻击，保持移动即可规避。</li>
        <li><strong>弱点阶段</strong>：当BOSS血量降至30%时，背部水晶会暴露，这是输出的最佳时机。</li>
      </ul>
      <p>击败矿道守卫者后，你将获得<strong>第一把BOSS武器"地脉之锤"</strong>和通往第二层的钥匙。</p>

      <h3>新手常见误区与避坑指南</h3>
      <ul>
        <li><strong>误区一</strong>：见到宝箱就开。有些宝箱其实是<strong>拟态怪</strong>伪装的，靠近前先观察宝箱是否有微弱的呼吸动画。</li>
        <li><strong>误区二</strong>：把所有材料都卖掉。初期铁矿石和草药看似不起眼，但中后期锻造和炼金需求量巨大。</li>
        <li><strong>误区三</strong>：忽视支线任务。很多支线任务会奖励<strong>独一无二的饰品</strong>，错过就无法再获取。</li>
        <li><strong>误区四</strong>：独自硬闯。部分隐藏BOSS需要特定职业配合才能击败，善用NPC队友系统。</li>
      </ul>

      <h3>总结</h3>
      <p>哥布林的洞窟是一款需要耐心和策略的游戏。不要急于求成，享受探索黑暗洞穴的每一步。当你逐渐熟悉这个世界后，你会发现那些曾经让你恐惧的黑暗角落，恰恰藏着最珍贵的宝藏。祝你在洞穴深处，找到属于自己的传奇。</p>
    `,
  },
  {
    slug: 'goblin-cave-survival-tips',
    title: '哥布林的洞窟生存法则：黑暗洞穴中的十大必备技巧',
    excerpt: '在伸手不见五指的洞穴中，知道如何生存比知道如何战斗更重要。本文总结十条经过实战验证的生存法则，涵盖光源管理、食物储备、陷阱识别与紧急逃生策略。',
    category: 'beginner-guide',
    imageSeq: 'article-beginner-02',
    imagePrompt: 'Adventurer camping by a small campfire inside a dark cave, cooking pot hanging over flames, survival gear spread on stone floor, warm firelight dancing on rough cave walls, dark fantasy survival scene with cozy yet tense atmosphere, painterly illustration style',
    date: '2026-07-10',
    readTime: '10分钟',
    author: '暗夜行者',
    tags: ['生存技巧', '资源管理', '进阶指南'],
    featured: false,
    contentHtml: `
      <h2>光源是你的生命线</h2>
      <p>在<strong>哥布林的洞窟</strong>中，光源不仅仅是为了看清道路——它直接影响你的战力、探索效率和生存概率。洞窟中有<strong>三种光照等级</strong>：明亮区、昏暗区和完全黑暗区。在完全黑暗区中，你的命中率会下降40%，而被怪物偷袭的概率翻倍。</p>
      <p>务必随身携带至少<strong>3根火把</strong>和<strong>2个荧光石</strong>。火把提供8分钟的明亮照明但会吸引怪物注意，荧光石光线较弱但不会引起敌意。根据场景灵活切换光源类型。</p>

      <h3>食物与水源管理</h3>
      <p>哥布林的洞窟有真实的饥饿和口渴系统。每进行30分钟冒险，你需要进食一次。饥饿状态下，你的最大生命值会逐步降低，最低降至正常值的60%。</p>
      <p><strong>地下蘑菇汤</strong>是最经济实惠的食物选择，用洞穴蘑菇和清水即可制作，回复40%饱食度并附带5分钟的缓慢回血效果。更高端的<strong>烤巨鼠肉串</strong>回复60%饱食度并提供10分钟的力量加成，但需要打败洞穴巨鼠才能获取食材。</p>

      <h3>陷阱识别技巧</h3>
      <p>洞窟中遍布各种致命陷阱，学会识别它们能让你少走很多弯路：</p>
      <ul>
        <li><strong>压力板陷阱</strong>：地面略微凸起的石板，颜色比周围略浅。靠近时仔细听，会有微弱的机械声。</li>
        <li><strong>绊索陷阱</strong>：横在走廊中间的细线，在火把光照下会反光。用远程武器射击绊索可以安全触发。</li>
        <li><strong>毒气陷阱</strong>：墙壁上不自然的小孔，周围通常有枯萎的植物。需要通过时屏住呼吸快速通过，或者用布料堵住口鼻。</li>
      </ul>

      <h3>紧急逃生策略</h3>
      <p>当你遭遇无法应对的强敌时，知道如何逃跑比盲目战斗明智得多：</p>
      <ul>
        <li>随身携带<strong>烟雾弹</strong>（可用火药粉+布片制作），投掷后创造5秒的视线遮挡。</li>
        <li>熟记每层的<strong>捷径和隐藏通道</strong>，很多看似死胡同的地方其实有可破坏的薄墙。</li>
        <li>使用<strong>回城卷轴</strong>可以瞬间返回最近的篝火点，但需要3秒读条且受到攻击会打断。</li>
      </ul>
      <p>记住，在哥布林的洞窟中，<strong>活着离开比英勇战死更有价值</strong>。每一次撤退都是为了下一次更好的进攻。</p>
    `,
  },
  {
    slug: 'goblin-cave-warrior-build',
    title: '哥布林的洞窟战士职业深度解析：最强坦克流派配装方案',
    excerpt: '战士是哥布林的洞窟中最受欢迎的职业之一。本文深度解析战士的三种主流流派——盾卫、狂战和圣骑的装备选择、技能搭配与实战打法，助你成为洞窟中最坚不可摧的存在。',
    category: 'beginner-guide',
    imageSeq: 'article-beginner-03',
    imagePrompt: 'Heavily armored warrior standing guard in dark dungeon corridor, massive shield reflecting torchlight, ornate plate armor with battle scars, defensive stance ready for combat, dark fantasy character portrait, dramatic chiaroscuro lighting with warm amber highlights',
    date: '2026-07-05',
    readTime: '15分钟',
    author: '铁壁要塞',
    tags: ['战士流派', '装备搭配', '坦克攻略'],
    featured: true,
    contentHtml: `
      <h2>战士职业概述</h2>
      <p>在<strong>哥布林的洞窟</strong>中，战士是最全面的近战职业。拥有全职业最高的生命值成长和物理防御，战士可以在前线为队友吸收大量伤害，同时也能在特定流派下爆发出惊人的输出。</p>

      <h3>流派一：盾卫——不朽的壁垒</h3>
      <p>盾卫是战士最经典的坦克流派，核心思路是<strong>吸引仇恨、承受伤害、保护队友</strong>。推荐装备：</p>
      <ul>
        <li><strong>武器</strong>：地脉守护者之盾 + 龙牙短矛。盾牌提供额外格挡率，短矛允许在持盾状态下攻击。</li>
        <li><strong>防具</strong>：全套黑曜石重甲套装。四件套效果：被击中时有20%概率触发"坚岩护体"，5秒内防御力翻倍。</li>
        <li><strong>饰品</strong>：不灭徽章（生命低于30%时自动回复50%血量，冷却3分钟）+ 嘲讽护符（扩大仇恨范围）。</li>
      </ul>
      <p>核心技能顺序：开怪先用<strong>战吼</strong>拉住全部仇恨→开启<strong>盾墙</strong>减伤→穿插<strong>盾击</strong>打断怪物施法→关键时刻用<strong>终极守护</strong>为全队套盾。</p>

      <h3>流派二：狂战——血怒中的毁灭者</h3>
      <p>如果你觉得盾卫太保守，狂战流派让你体验什么叫做<strong>以血换血、越战越勇</strong>。狂战的核心机制是血量越低伤害越高：</p>
      <ul>
        <li><strong>武器</strong>：双持裂地战斧。两把斧头的攻速加成叠加，配合狂战被动"嗜血"可实现极高攻速。</li>
        <li><strong>防具</strong>：狂战士轻甲。牺牲防御换取暴击率和移动速度，适合操作娴熟的玩家。</li>
        <li><strong>饰品</strong>：血怒之环（每损失10%生命值，伤害提升8%）+ 绝望护腕（血量低于20%时进入狂暴状态）。</li>
      </ul>

      <h3>流派三：圣骑——光明中的审判者</h3>
      <p>圣骑士是战士的进阶转职方向，结合了物理攻击与神圣魔法，能打能奶，是最全能的流派：</p>
      <ul>
        <li><strong>武器</strong>：圣光之锤 + 祝福圣典。锤子造成范围神圣伤害，圣典提供治疗和buff。</li>
        <li><strong>防具</strong>：圣殿骑士板甲。提供均衡的物理和魔法防御，四件套效果：使用治疗技能后下次攻击必定暴击。</li>
      </ul>
      <p>圣骑的强势期在中后期，当<strong>神圣能量</strong>叠满时，可以施放全屏AOE技能"圣光裁决"，对不死族怪物造成300%伤害。</p>
    `,
  },
  {
    slug: 'goblin-cave-elara-story',
    title: '哥布林的洞窟核心角色深度解读：艾拉娜——被诅咒的月光精灵',
    excerpt: '艾拉娜是哥布林的洞窟中最具人气的角色之一。这位月光精灵背后隐藏着怎样的悲剧故事？她的技能树如何搭配才能发挥最大战力？本篇为你一一揭晓。',
    category: 'characters',
    imageSeq: 'article-char-01',
    imagePrompt: 'Elegant moon elf female character with silver hair and pale skin in dark cave setting, ethereal blue-white glow emanating from her hands, wearing flowing elven robes with lunar motifs, melancholic expression, dark fantasy character art, soft moonlight contrasting with cave darkness',
    date: '2026-07-20',
    readTime: '14分钟',
    author: '星辰叙事者',
    tags: ['角色攻略', '艾拉娜', '月光精灵'],
    featured: true,
    contentHtml: `
      <h2>艾拉娜的背景故事</h2>
      <p>艾拉娜·月影，最后一位<strong>月光精灵</strong>的后裔。在哥布林的洞窟世界观中，月光精灵一族在一千年前的"暗月之战"中几乎被哥布林之王图尔格屠戮殆尽。年幼的艾拉娜被母亲的魔法封印保护，沉睡在洞穴深处的水晶棺中，直到被冒险者无意中唤醒。</p>
      <p>她身上携带着族人最后的<strong>月神诅咒</strong>——每当月圆之夜，诅咒会让她陷入狂乱，但同时赋予她超越极限的战斗能力。这个独特的机制也反映在她的游戏技能设计中。</p>

      <h3>艾拉娜技能树解析</h3>
      <p>艾拉娜的技能分为<strong>月光系（辅助/治疗）</strong>和<strong>暗月系（输出/控制）</strong>两大分支：</p>
      <ul>
        <li><strong>月辉治愈（主动）</strong>：消耗30法力，为单体目标回复大量生命值。月光精灵的招牌治疗技能，后期升级后附带净化debuff效果。</li>
        <li><strong>银月箭雨（主动）</strong>：消耗50法力，向目标区域降下月光箭矢，造成范围魔法伤害并减速敌人。对付成群小怪的神技。</li>
        <li><strong>月神诅咒（被动）</strong>：当艾拉娜的生命值低于40%时，自动进入诅咒状态。攻击力提升50%，但每秒损失2%生命值。高风险高回报的核心被动。</li>
        <li><strong>永恒月光（终极）</strong>：消耗全部法力，召唤月光笼罩全场。所有友方单位获得持续回血和伤害减免，持续15秒。</li>
      </ul>

      <h3>最佳装备搭配</h3>
      <p>艾拉娜的装备路线取决于你选择主修月光还是暗月：</p>
      <ul>
        <li><strong>月光流</strong>：优先堆叠治疗加成和法力回复。推荐武器"月神权杖"+ 防具"银叶长袍"+ 饰品"月泪项链"。</li>
        <li><strong>暗月流</strong>：优先暴击率和法术穿透。推荐双持"暗月双刃"+ 防具"影纱斗篷"+ 饰品"诅咒之环"。</li>
        <li><strong>均衡流</strong>（推荐新手）：武器"暮光长弓"+ 防具"月影轻甲"+ 饰品"精灵之泪"。这套装备让艾拉娜可奶可输出，适应各种战斗场景。</li>
      </ul>
      <p>艾拉娜的上手难度中等，她的魅力在于<strong>月神诅咒的时机把控</strong>——何时让自己进入低血量状态获取伤害加成，何时保持安全距离进行治疗，是区分高手和新手的关键。</p>
    `,
  },
  {
    slug: 'goblin-cave-gorak-character',
    title: '哥布林的洞窟隐藏角色揭秘：兽人战士格拉克的解锁与培养',
    excerpt: '格拉克是哥布林的洞窟中的隐藏可玩角色。这位被族人流放的兽人战士拥有全游戏最高的物理攻击成长，但解锁条件极为苛刻。本文将详细讲解格拉克的完整解锁流程与最佳培养路线。',
    category: 'characters',
    imageSeq: 'article-char-02',
    imagePrompt: 'Massive orc warrior character with green-gray skin and tribal tattoos, holding a giant battle axe, standing in dark cave with defiant expression, battle-worn leather armor with bone ornaments, dark fantasy character concept art, dramatic side lighting emphasizing muscular physique',
    date: '2026-07-18',
    readTime: '11分钟',
    author: '兽人研究协会',
    tags: ['隐藏角色', '格拉克', '兽人战士'],
    featured: false,
    contentHtml: `
      <h2>格拉克的背景</h2>
      <p>格拉克·碎骨者曾是<strong>血斧兽人部落</strong>最年轻的战争酋长。在一次对哥布林洞窟的远征中，他的部队遭遇了哥布林之王图尔格的伏击。格拉克是唯一的幸存者，但由于"被哥布林击败"的耻辱，他被部落正式流放。如今他独自徘徊在洞窟第三层，等待着复仇的机会。</p>

      <h3>解锁条件</h3>
      <p>要解锁格拉克，你需要完成以下步骤（所有步骤必须在<strong>同一周目</strong>内完成）：</p>
      <ol>
        <li>在第三层"兽人营地"废墟中找到<strong>破碎的战旗</strong>（隐藏房间，需要从篝火处向左走三个房间后破坏右侧墙壁）。</li>
        <li>携带战旗击败第三层的BOSS<strong>洞穴巨蛛</strong>。</li>
        <li>在BOSS战后不要离开房间，等待30秒。格拉克会从暗处现身。</li>
        <li>与格拉克对话并选择"我愿意与你并肩作战"（注意：选择其他选项将永久错过解锁机会）。</li>
      </ol>

      <h3>格拉克的战斗特点</h3>
      <p>格拉克是纯粹的<strong>物理输出机器</strong>。他的所有技能都围绕一个核心机制——<strong>怒气系统</strong>。攻击和被攻击都会积累怒气，怒气满值后可以释放威力巨大的终结技。</p>
      <ul>
        <li><strong>碎骨猛击</strong>：基础攻击技能，消耗10怒气。对单体目标造成高额物理伤害，有30%概率造成"骨折"debuff（降低目标攻击力）。</li>
        <li><strong>战争践踏</strong>：消耗25怒气。震击地面造成范围伤害并击倒周围敌人。</li>
        <li><strong>血斧投掷</strong>：消耗15怒气。唯一的远程技能，投掷飞斧造成穿刺伤害。</li>
        <li><strong>先祖之怒（终极）</strong>：消耗100怒气。进入狂暴状态，攻击速度和移动速度翻倍，持续20秒。期间所有攻击附带吸血效果。</li>
      </ul>
      <p>格拉克的大弱点是没有自疗手段（除了终极技能的吸血），所以他非常依赖队伍中的治疗者。搭配艾拉娜或装备吸血饰品可以弥补这个短板。</p>
    `,
  },
  {
    slug: 'goblin-cave-npc-guide',
    title: '哥布林的洞窟全NPC图鉴：商人、铁匠与神秘旅人的位置与功能',
    excerpt: '洞窟中的NPC不只提供对话，他们是你冒险路上最重要的助力。本文整理了所有NPC的刷新位置、可购买物品、任务触发条件以及隐藏好感度系统的完整攻略。',
    category: 'characters',
    imageSeq: 'article-char-03',
    imagePrompt: 'Collection of fantasy NPC characters in a dark underground tavern setting, dwarf blacksmith at forge, mysterious hooded merchant at counter, old wizard reading scrolls, warm firelight illuminating rough stone interior, dark fantasy gathering scene with rich character details',
    date: '2026-07-12',
    readTime: '13分钟',
    author: '洞穴商队',
    tags: ['NPC攻略', '商人', '好感度'],
    featured: false,
    contentHtml: `
      <h2>洞窟中的NPC们</h2>
      <p>在<strong>哥布林的洞窟</strong>中，你不是孤军奋战。散布在各层的NPC们提供着从装备交易到情报分享的各种服务。了解他们的位置、功能和隐藏任务，会让你的冒险事半功倍。</p>

      <h3>第一层：废弃矿道</h3>
      <ul>
        <li><strong>老矿工汉斯</strong>（篝火旁固定刷新）：出售基础药水和火把。完成他的支线任务"寻找遗失的矿镐"可获得稀有饰品"矿工之戒"（采矿收益+30%）。</li>
        <li><strong>流浪商人莉娜</strong>（随机刷新在三个位置之一）：出售随机稀有道具，价格浮动。好感度达到3级后，她会开始出售传奇级物品。</li>
      </ul>

      <h3>第二层：幽暗地窟</h3>
      <ul>
        <li><strong>铁匠大师格鲁姆</strong>（铁匠铺固定位置）：提供装备强化和修理服务。收集到足够的"暗钢矿石"后可以解锁专属武器锻造。</li>
        <li><strong>炼金术士薇拉</strong>（隐藏房间）：需先在第三层找到炼金配方才能解锁她的商店。出售高级药水和附魔材料。</li>
      </ul>

      <h3>第三层：失落之城</h3>
      <ul>
        <li><strong>神秘旅人阿卡什</strong>（随机出现在城市废墟中）：已知最重要的NPC之一。他以情报交换稀有物品，提供隐藏BOSS的位置信息。</li>
        <li><strong>被囚的学者伊恩</strong>（监狱区域）：救出他后，他会回到营地并提供怪物图鉴功能，显示已遭遇敌人的详细弱点和掉落物品。</li>
      </ul>

      <h3>隐藏好感度系统</h3>
      <p>每位NPC都有一个<strong>隐藏的好感度数值</strong>（0-100）。好感度影响商品价格、解锁物品的品质以及是否可以触发专属任务。提升好感度的方式包括：</p>
      <ul>
        <li>在NPC处消费金币（每100金币+1好感）</li>
        <li>完成该NPC的支线任务（+10到+30好感不等）</li>
        <li>赠送NPC喜好的礼物（每个NPC有不同的喜好，需要自行探索）</li>
      </ul>
      <p>好感度达到80以上时，NPC会送你一件<strong>专属传说级物品</strong>，每个NPC只送一次，错过不会再有。</p>
    `,
  },
  {
    slug: 'goblin-cave-lore-deep-dive',
    title: '哥布林的洞窟世界观全解析：千年前暗月之战的真相',
    excerpt: '哥布林的洞窟的剧情远比你想象的复杂。千年前那场改变了整个地下世界的暗月之战，到底隐藏着怎样的秘密？哥布林之王图尔格的崛起背后又有何等惊人的阴谋？',
    category: 'story',
    imageSeq: 'article-story-01',
    imagePrompt: 'Ancient stone mural carved into cave wall depicting epic battle scene between elves and goblin armies under a dark moon, mysterious runes and glowing crystals embedded in the carving, torchlight revealing hidden details, dark fantasy archaeological discovery scene, rich amber lighting',
    date: '2026-08-01',
    readTime: '18分钟',
    author: '洞穴史学家',
    tags: ['世界观', '暗月之战', '剧情深度'],
    featured: true,
    contentHtml: `
      <h2>被尘封的千年历史</h2>
      <p>当大多数冒险者还在为如何击败下一个BOSS而苦恼时，很少有人真正去探究<strong>哥布林的洞窟</strong>背后那段波澜壮阔的历史。今天，让我们走进那些被遗忘的岁月。</p>

      <h3>暗月之战的起因</h3>
      <p>一千年前，哥布林的洞窟并非如今这般阴暗荒芜。那时，地下世界由<strong>五大种族</strong>共同统治：月光精灵、深渊矮人、暗夜精灵、石心巨人和洞穴人类。他们建立了繁荣的地下城邦——<strong>阿格隆达</strong>。</p>
      <p>然而，和平在"暗月之日"被打破。一场突如其来的天体异变使得地下世界的魔法能量失控，诞生了大量变异生物。在这些变异生物中，最强大也最邪恶的，便是后来的<strong>哥布林之王图尔格</strong>。</p>
      <p>图尔格并非普通哥布林。他是由五大种族共同的负面情绪——贪婪、嫉妒、仇恨、恐惧和傲慢——凝聚而成的<strong>魔法造物</strong>。他拥有将其他生物转化为哥布林仆从的可怕能力，短短数年内就组建了一支庞大的哥布林军团。</p>

      <h3>五大种族的覆灭</h3>
      <p>暗月之战持续了整整三十年。五大种族虽然各自拥有强大的力量，但由于图尔格巧妙的离间计，他们始终无法形成有效的联合：</p>
      <ul>
        <li><strong>月光精灵</strong>首当其冲。图尔格首先进攻月光精灵的领地，利用月光精灵对纯净魔法的依赖，用暗影魔法污染了他们的力量源泉——月井。艾拉娜的母亲，月光女王瑟琳迪尔，在最后时刻用生命封印了女儿。</li>
        <li><strong>深渊矮人</strong>躲入更深的地层，但被图尔格用地震魔法封锁了所有出口。如今他们是否还在地下深处存活，无人知晓。</li>
        <li><strong>石心巨人</strong>被图尔格用计引出领地后逐个击破。传说最后一尊石心巨人化作了洞窟第七层的石山，其心脏仍在跳动。</li>
      </ul>

      <h3>图尔格的真正目的</h3>
      <p>很多玩家以为图尔格只是一个想统治地下世界的暴君，但游戏中的隐藏文本揭示了更深的真相：<strong>图尔格在寻找"原初之种"</strong>——传说中创造整个地下世界的核心神器。拥有原初之种，就可以重塑整个地下世界的规则。</p>
      <p>这个惊天秘密藏在地图第五层"遗忘图书馆"的禁书区中，需要收集全部12块知识碎片才能解锁阅读权限。解开这个谜团的玩家，也将解锁游戏的<strong>隐藏真结局路线</strong>。</p>
    `,
  },
  {
    slug: 'goblin-cave-map-layer1-3',
    title: '哥布林洞窟地图完全攻略：第一至三层全区域标注与秘密通道',
    excerpt: '本文为你提供哥布林洞窟前三层的完整地图标注，包括所有宝箱位置、隐藏房间入口、陷阱分布和BOSS房间路径。配合详细文字说明，让你不再在黑暗洞穴中迷失方向。',
    category: 'map-exploration',
    imageSeq: 'article-map-01',
    imagePrompt: 'Weathered parchment style fantasy dungeon map with detailed room layouts and mysterious markings, compass rose in corner, candle wax drips on edges, torchlight reflecting off the aged paper surface, cartography aesthetic with sepia and warm brown tones, dark ambient atmosphere',
    date: '2026-07-25',
    readTime: '16分钟',
    author: '制图大师',
    tags: ['地图攻略', '隐藏房间', '宝箱位置'],
    featured: true,
    contentHtml: `
      <h2>阅读本攻略前的准备</h2>
      <p>在开始探索之前，建议先在游戏中打开<strong>地图标记功能</strong>（快捷键M）。本文中提到的所有位置都将以篝火点为参照物，使用"方位+房间数"的方式进行描述。</p>

      <h3>第一层：废弃矿道（推荐等级1-10）</h3>
      <p>第一层的整体布局呈<strong>环形结构</strong>，以中央大厅的篝火为起点，向外辐射三条主矿道。</p>
      <ul>
        <li><strong>东侧矿道</strong>：新手友好路线。沿途3个普通宝箱，入口处就是老矿工汉斯。尽头是通往第二层的正门（需击败矿道守卫者）。</li>
        <li><strong>西侧矿道</strong>：资源丰富路线。沿途有大量矿脉可采集铁矿石和铜矿石。隐藏房间在第三个岔路口向左的墙壁后面（用炸药或重型武器可破坏），内含<strong>黄金宝箱</strong>（随机掉落蓝色品质装备）。</li>
        <li><strong>北侧矿道</strong>：挑战路线。怪物等级偏高但有<strong>2个白银宝箱</strong>。在矿道中段有一个隐藏的升降平台，可以到达第一层的地下夹层，里面有第一层的唯一一个<strong>传说宝箱</strong>，但守护怪物为10级的精英石魔像。</li>
      </ul>

      <h3>第二层：幽暗地窟（推荐等级10-20）</h3>
      <p>第二层的核心是<strong>中央钟乳石大厅</strong>，铁匠大师格鲁姆的铁匠铺就位于此处。从大厅向外延伸四条通道：</p>
      <ul>
        <li><strong>南侧通道（主路线）</strong>：通往第三层的必经之路。BOSS洞穴巨蛛在此路线的尽头。途中有一个<strong>必开的白银宝箱</strong>在瀑布后面。</li>
        <li><strong>东侧通道（支线）</strong>：通往地下湖区域。湖中有可垂钓的稀有鱼类，湖底沉船内有<strong>黄金宝箱</strong>（需要潜水能力或水下呼吸药水）。</li>
        <li><strong>北侧通道（隐藏）</strong>：入口被巨型蘑菇遮挡，需要用火属性攻击烧掉蘑菇才能发现。通道尽头是一处<strong>远古祭坛</strong>，献祭特定物品可获得永久属性加成。</li>
        <li><strong>西侧通道（捷径）</strong>：激活后可从第一层直接跳到此位置，大幅缩短跑图时间。</li>
      </ul>

      <h3>第三层：失落之城（推荐等级20-30）</h3>
      <p>第三层是洞窟中规模最大的区域，曾经是<strong>深渊矮人的首都</strong>。城市废墟被分为三个大区：</p>
      <ul>
        <li><strong>王城区</strong>：城市中心，BOSS"皇家石像鬼"所在地。王宫废墟中有<strong>3个黄金宝箱和1个传说宝箱</strong>，但传说宝箱需要解开王座厅的机关谜题。</li>
        <li><strong>工匠区</strong>：矮人锻造场的废墟。可以在这里找到稀有矿石和锻造配方。炼金术士薇拉的隐藏房间位于此处一座坍塌高塔的地下室中。</li>
        <li><strong>居民区</strong>：被囚的学者伊恩位于此区的监狱废墟中。这里也是支线任务"失落的矮人遗物"的主要收集区域。</li>
      </ul>
      <p><strong>重点提示</strong>：第三层有一个全游戏最隐蔽的<strong>秘密商店</strong>——在工匠区和居民区的交界处，有一面看似普通的石墙，在游戏时间午夜时分（00:00-02:00）会短暂变为可穿过的幻影墙。里面的神秘商人出售无法在其他任何地方获得的独特物品。</p>
    `,
  },
  {
    slug: 'goblin-cave-secret-rooms',
    title: '哥布林的洞窟全隐藏房间大揭秘：37个隐藏区域的位置与进入方法',
    excerpt: '你知道哥布林的洞窟中有多达37个隐藏房间吗？从可破坏的墙壁到需要特定道具才能开启的魔法门，本文为你详细标注每一个隐藏区域的位置和进入条件。',
    category: 'map-exploration',
    imageSeq: 'article-map-02',
    imagePrompt: 'Adventurer discovering a hidden passage behind crumbling stone wall in dark cave, glowing runes on the floor revealing secret door mechanism, treasure chest visible through the gap, sense of discovery and mystery, dark fantasy exploration scene with warm torchlight',
    date: '2026-07-22',
    readTime: '20分钟',
    author: '秘密猎手',
    tags: ['隐藏房间', '秘密区域', '收集指南'],
    featured: false,
    contentHtml: `
      <h2>为什么要寻找隐藏房间</h2>
      <p>在<strong>哥布林的洞窟</strong>中，隐藏房间不仅仅是几个额外的宝箱那么简单。其中一些隐藏房间包含了<strong>独一无二的传说级物品</strong>、<strong>永久属性加成</strong>、甚至是<strong>影响主线结局的关键道具</strong>。错过一个隐藏房间，你可能就错过了整个游戏最精彩的部分。</p>

      <h3>隐藏房间的四种类型</h3>
      <ul>
        <li><strong>可破坏墙壁</strong>（最常见，共21处）：墙壁上细微的裂缝是其标志。用重型武器、炸药或特定技能可以破坏。小技巧：将火把靠近墙壁，裂缝处会泛出微弱的红光。</li>
        <li><strong>机关门</strong>（共8处）：需要通过踩踏压力板、拉动拉杆或按照正确顺序激活符文来开启。大多数机关门的解法可以在附近的壁画或雕刻中找到线索。</li>
        <li><strong>魔法幻影墙</strong>（共5处）：看起来是普通墙壁但可以直接穿过。只有使用"真视药水"或装备"洞察之眼"饰品才能看到它们的真实面目。</li>
        <li><strong>条件触发门</strong>（共3处）：只在满足特定条件时出现，如特定时间、携带特定物品或击败特定BOSS后。</li>
      </ul>

      <h3>不可错过的五大隐藏房间</h3>
      <ul>
        <li><strong>第一层·黄金巢穴</strong>：西侧矿道第三岔口左侧墙壁后。内含<strong>金蛋饰品</strong>（每分钟自动产生50金币），这个饰品越早拿到越好。</li>
        <li><strong>第二层·月神祭坛</strong>：地下湖沉船底部。需要先收集三块月之碎片（分别在第一、二、三层）。祭坛赋予角色<strong>永久+10%魔法抗性</strong>。</li>
        <li><strong>第三层·秘密商店</strong>：工匠区与居民区交界处的午夜幻影墙。神秘商人每7天刷新一次商品，可能刷出<strong>全游戏最强装备</strong>。</li>
        <li><strong>第四层·知识殿堂</strong>：遗忘图书馆的禁书区。需要解锁12块知识碎片。内含<strong>神秘书籍"原初之语"</strong>，是解锁真结局的关键道具。</li>
        <li><strong>第五层·龙王陵墓</strong>：BOSS龙王的王座后方隐藏。内含<strong>龙鳞套装</strong>，全游戏物理防御最高的防具，但对火焰攻击有-20%抗性的负面效果。</li>
      </ul>
    `,
  },
  {
    slug: 'goblin-cave-equipment-tier-list',
    title: '哥布林的洞窟装备天梯榜：全武器与防具强度排名及获取方式',
    excerpt: '从新手铁剑到传说中的灭神之刃，本文整理了哥布林的洞窟中全部128件装备的详细数据与强度排名。附带每件装备的获取方式和推荐搭配，是装备收集党的必备参考。',
    category: 'equipment',
    imageSeq: 'article-equip-01',
    imagePrompt: 'Collection of fantasy weapons and armor displayed on tiered pedestals in dark underground vault, legendary sword glowing with golden light at the top, various shields axes and staves on lower tiers, treasure vault atmosphere with warm torchlight reflecting off metallic surfaces',
    date: '2026-07-28',
    readTime: '22分钟',
    author: '装备猎人',
    tags: ['装备排名', '武器图鉴', '防具推荐'],
    featured: true,
    contentHtml: `
      <h2>装备品质与等级体系</h2>
      <p><strong>哥布林的洞窟</strong>中的装备分为六个品质等级：<strong>普通（白）、优秀（绿）、稀有（蓝）、史诗（紫）、传说（橙）、神话（红）</strong>。其中神话品质装备全游戏仅有5件，每件都有独一无二的特殊效果。</p>

      <h3>S级武器（版本最强）</h3>
      <ul>
        <li><strong>灭神之刃（神话·剑）</strong>：基础攻击力280，特殊效果"神灭"——对BOSS怪物造成双倍伤害。获取方式：收集5块灭神碎片后在铁匠处合成。碎片分布于各层隐藏房间和BOSS掉落。</li>
        <li><strong>龙炎法杖（神话·杖）</strong>：基础魔力220，特殊效果"龙息"——所有火系魔法伤害+60%并附带灼烧dot。获取方式：击败第七层BOSS远古炎龙（5%概率掉落）。</li>
        <li><strong>暗月双刃（传说·双剑）</strong>：基础攻击力210，特殊效果"月蚀"——夜晚时攻击速度+40%，暴击率+15%。获取方式：艾拉娜专属任务线终章奖励。</li>
      </ul>

      <h3>最强防具推荐</h3>
      <ul>
        <li><strong>龙鳞套装（传说·重甲）</strong>：物理防御全游戏最高。四件套效果：受到物理伤害时反弹15%给攻击者。</li>
        <li><strong>暗影编织者（传说·轻甲）</strong>：提供最高的闪避率和移动速度加成。四件套效果：闪避后下一次攻击必定暴击。</li>
        <li><strong>永恒法袍（神话·法袍）</strong>：唯一的红色品质防具。特殊效果"永恒"——死亡后立即满血复活（每次冒险限触发一次）。获取方式：完成全部37个隐藏房间的探索成就。</li>
      </ul>

      <h3>新手过渡期装备路线</h3>
      <p>不建议新手直接追求顶级装备。推荐的过渡路线：初期使用<strong>矿工套装（白→绿）</strong>→ 10级后制作<strong>黑铁套装（蓝）</strong>→ 20级后收集<strong>暗钢套装（紫）</strong>→ 30级后开始追求传说和神话装备。</p>
    `,
  },
  {
    slug: 'goblin-cave-crafting-guide',
    title: '哥布林的洞窟锻造炼金全攻略：从矿石到神器的制作之路',
    excerpt: '哥布林的洞窟的锻造和炼金系统远比表面看起来复杂。本文详解所有锻造配方、材料获取路线、附魔机制以及炼金药水的完整制作指南，教你如何用最少的材料做出最强的装备。',
    category: 'equipment',
    imageSeq: 'article-equip-02',
    imagePrompt: 'Dwarf blacksmith working at fiery forge in underground cave workshop, hammer striking glowing metal on anvil, various crafted weapons hanging on stone walls, alchemy table with bubbling potions nearby, dark fantasy crafting scene with warm forge fire illuminating the workspace',
    date: '2026-07-24',
    readTime: '15分钟',
    author: '锻造大师',
    tags: ['锻造系统', '炼金配方', '材料收集'],
    featured: false,
    contentHtml: `
      <h2>锻造系统基础</h2>
      <p>在<strong>哥布林的洞窟</strong>中，大部分高品质装备并非怪物掉落，而是通过<strong>锻造系统</strong>自行制作。锻造需要三个要素：配方、材料和足够的锻造等级。</p>

      <h3>锻造等级提升</h3>
      <p>锻造等级通过制作物品来提升，从1级到50级。每次成功锻造获得经验值=物品品质系数×材料稀有度。快速提升锻造等级的方法是批量制作<strong>铁锭</strong>（虽然单个经验少但材料便宜量大）。</p>

      <h3>必学锻造配方</h3>
      <ul>
        <li><strong>暗钢合金</strong>（锻造等级15解锁）：暗铁矿石×5 + 暗影精华×1。暗钢是制作紫色品质装备的基础材料，需求量极大。</li>
        <li><strong>附魔核心</strong>（锻造等级25解锁）：月长石×3 + 魔力粉尘×10。用于给装备附加魔法属性。</li>
        <li><strong>龙鳞合金</strong>（锻造等级40解锁）：龙鳞碎片×10 + 熔岩精华×3 + 暗钢合金×5。制作传说品质装备的关键材料。</li>
      </ul>

      <h3>炼金系统详解</h3>
      <p>炼金术是另一个重要的制作系统，主要产出各类药水和附魔材料：</p>
      <ul>
        <li><strong>生命药水（大）</strong>：洞穴蘑菇×3 + 清水×1 + 玻璃瓶×1。回复50%最大生命值。</li>
        <li><strong>力量药水</strong>：龙血草×2 + 巨魔苔藓×1 + 酒精×1。30分钟内攻击力+25%。</li>
        <li><strong>隐身药水</strong>：幽灵菇×2 + 暗影粉尘×1 + 清水×1。提供60秒隐身效果，但攻击会打破隐身。</li>
      </ul>
      <p>炼金的关键在于<strong>材料的新鲜度</strong>。采集后的材料随时间推移会降低效力，使用新鲜材料制作的药水效果会提升20%。</p>
    `,
  },
  {
    slug: 'goblin-cave-boss-spider-queen',
    title: '哥布林的洞窟BOSS攻略：洞穴蛛后——新手的第一道真正考验',
    excerpt: '洞穴蛛后是第二层的最终BOSS，也是很多新手玩家遇到的第一个真正的挑战。本文详细拆解蛛后的所有技能机制、阶段转换时机、场地利用技巧以及推荐阵容搭配。',
    category: 'boss-guide',
    imageSeq: 'article-boss-01',
    imagePrompt: 'Giant spider queen boss in dark underground cavern, massive web spanning across the chamber, glowing red eyes and crystalline carapace reflecting torchlight, adventurers in defensive formation below, epic dark fantasy boss battle scene with dramatic amber and crimson lighting',
    date: '2026-07-30',
    readTime: '12分钟',
    author: 'BOSS狩猎队',
    tags: ['BOSS攻略', '洞穴蛛后', '第二层'],
    featured: true,
    contentHtml: `
      <h2>洞穴蛛后——新手的噩梦</h2>
      <p>如果你在第一层击败矿道守卫者后觉得自己已经无敌了，<strong>洞穴蛛后</strong>一定会让你重新认识这个游戏。作为第二层的最终BOSS，蛛后的战斗设计充满了恶意，但掌握了规律后，这场战斗可以变得非常精彩。</p>

      <h3>BOSS基础数据</h3>
      <ul>
        <li>等级：15级</li>
        <li>生命值：8,500</li>
        <li>抗性：对毒属性攻击免疫，对火属性攻击弱（伤害+50%）</li>
        <li>推荐等级：12级以上</li>
      </ul>

      <h3>技能机制详解</h3>
      <p>蛛后有<strong>五个核心技能</strong>，战斗分为三个阶段，每个阶段新增一个技能：</p>

      <h4>第一阶段（100%-70%血量）</h4>
      <ul>
        <li><strong>毒液喷射</strong>：直线范围攻击，有明显的前摇（蛛后仰头并发出嘶嘶声）。向侧面翻滚即可躲避。中毒后每秒损失3%最大生命值，持续8秒，<strong>必须立刻使用解毒药</strong>。</li>
        <li><strong>蛛网束缚</strong>：向玩家当前位置喷射蛛网，命中后禁锢3秒。看到蛛后腹部发出白光立即向侧方闪避。</li>
      </ul>

      <h4>第二阶段（70%-40%血量）</h4>
      <ul>
        <li><strong>召唤小蜘蛛</strong>：蛛后后退到场地边缘，召唤6只小型洞穴蜘蛛。小蜘蛛虽然血量低但数量多，建议使用范围技能快速清理。注意：如果30秒内未清理完小蜘蛛，它们会自爆造成大量伤害。</li>
      </ul>

      <h4>第三阶段（40%-0%血量）</h4>
      <ul>
        <li><strong>狂暴突袭</strong>：蛛后进入狂暴状态，攻击速度和移动速度翻倍。此时它的攻击欲望极强，建议不要贪刀。在它完成三连击后有2秒的硬直，这是输出的最佳窗口。</li>
        <li><strong>毒雾弥漫</strong>：全场范围毒雾，持续造成伤害。必须在这之前将蛛后引到场地边缘的三个通风口附近，通风口会吹散毒雾创造安全区域。</li>
      </ul>

      <h3>推荐阵容</h3>
      <ul>
        <li><strong>坦克</strong>：战士（盾卫流派），负责吸引仇恨和打断技能。</li>
        <li><strong>输出</strong>：法师（火系专精），蛛后的火属性弱点让法师在这场战斗中输出爆炸。</li>
        <li><strong>治疗</strong>：艾拉娜（月光系），稳定的治疗和净化能力对抗毒属性极其重要。</li>
      </ul>
      <p>击败洞穴蛛后掉落的<strong>"蛛丝斗篷"</strong>（史诗品质）提供全游戏最高的毒抗和不错的闪避率，在后续很多BOSS战中都有奇效，务必获取。</p>
    `,
  },
  {
    slug: 'goblin-cave-boss-dragon-king',
    title: '哥布林的洞窟最终BOSS攻略：哥布林之王图尔格的终极对决',
    excerpt: '图尔格，哥布林之王，整个洞窟的最终主宰。这场终极对决分为五个阶段，每个阶段都有完全不同的机制。本攻略将揭示图尔格的所有弱点、隐藏机制以及达成隐藏结局的特殊条件。',
    category: 'boss-guide',
    imageSeq: 'article-boss-02',
    imagePrompt: 'Epic final boss confrontation with massive goblin king on dark throne surrounded by treasure and glowing crystals, hero standing defiantly with sword raised, dramatic lighting with intense golden and emerald energy swirling, cinematic dark fantasy battle scene with epic scale',
    date: '2026-08-05',
    readTime: '20分钟',
    author: '终末之刃',
    tags: ['最终BOSS', '图尔格', '隐藏结局'],
    featured: true,
    contentHtml: `
      <h2>最终对决——你准备好了吗</h2>
      <p>经历了漫长的洞窟冒险，你终于站到了<strong>哥布林之王图尔格</strong>的面前。这场战斗不仅是对你装备和等级的考验，更是对你对整个游戏理解深度的终极检验。</p>

      <h3>战斗前置条件</h3>
      <p>在挑战图尔格之前，确保你完成以下准备：</p>
      <ul>
        <li>角色等级至少达到<strong>45级</strong>（推荐50级满级）</li>
        <li>携带至少<strong>20瓶超级生命药水</strong>和<strong>10瓶解毒药</strong></li>
        <li>装备至少全部史诗品质以上</li>
        <li>如果追求隐藏结局，必须携带道具<strong>"原初之语"</strong></li>
      </ul>

      <h3>五阶段战斗详解</h3>

      <h4>第一阶段：王者威压（100%-80%）</h4>
      <p>图尔格坐在王座上，仅使用远程魔法攻击。这个阶段相对简单，边躲避能量弹边接近王座即可。注意地面上周期性出现的暗影漩涡，踩到会受到持续伤害。打掉20%血量后进入第二阶段。</p>

      <h4>第二阶段：黑暗军团（80%-60%）</h4>
      <p>图尔格起身并召唤哥布林军团。这个阶段需要同时应对BOSS本体和不断刷新的哥布林小兵。优先击杀<strong>哥布林萨满</strong>（它们会治疗BOSS），然后清理近战小兵，最后再输出BOSS。</p>

      <h4>第三阶段：暗月降临（60%-35%）</h4>
      <p>场地变为完全黑暗，只有图尔格双眼的红光可见。使用荧光石或夜视药水！BOSS会从黑暗中突然袭击，需要在它攻击的瞬间进行<strong>精确格挡或闪避</strong>才能创造反击机会。这个阶段不要求输出，生存是第一要务。</p>

      <h4>第四阶段：融合形态（35%-15%）</h4>
      <p>图尔格与暗月之力融合，体型增大三倍。所有攻击变为范围伤害。场地边缘会出现四个魔法水晶，击败水晶可以削弱BOSS的防御。这个阶段需要分工合作——两人吸引BOSS注意，两人清理水晶。</p>

      <h4>第五阶段：绝望终局（15%-0%）</h4>
      <p>图尔格释放终极大招"暗月陨落"——全场范围即死攻击，但有15秒的施法时间。你必须在15秒内全力输出将其击杀，否则全队阵亡。此时不要留任何技能，把所有大招和增益药水全部用上。</p>

      <h3>隐藏结局触发条件</h3>
      <p>携带<strong>"原初之语"</strong>进入战斗后，在图尔格第五阶段被打到5%血量时，会出现特殊对话选项。选择"我知道你的痛苦，图尔格"即可触发隐藏结局，揭示图尔格背后的悲剧真相和整个地下世界真正的命运。这个结局将解锁<strong>New Game+</strong>模式和全游戏唯一的神话品质武器<strong>——"原初之光"</strong>。</p>
    `,
  },
  {
    slug: 'goblin-cave-story-hidden-endings',
    title: '哥布林的洞窟三大结局全解析：普通、真与隐藏结局的触发条件',
    excerpt: '哥布林的洞窟有三个完全不同的结局路线。普通结局只是开始，真结局揭示世界的真相，而隐藏结局将改变一切。本文详细列出三条路线的全部分支点和关键选择。',
    category: 'story',
    imageSeq: 'article-story-02',
    imagePrompt: 'Mysterious glowing portal opening in dark cave chamber, three branching paths illuminated by different colored magical lights, golden warm light on one path, emerald green on another, deep crimson on the third, symbolic crossroads scene, dark fantasy atmospheric composition',
    date: '2026-08-03',
    readTime: '16分钟',
    author: '结局猎人',
    tags: ['结局路线', '隐藏结局', '多周目'],
    featured: false,
    contentHtml: `
      <h2>三大结局总览</h2>
      <p><strong>哥布林的洞窟</strong>拥有三条完全不同的结局路线，每一条都揭示了这个世界不同的侧面。总计游戏时长约40小时的冒险，最终走向何方，取决于你在关键节点做出的选择。</p>

      <h3>结局一：征服者（普通结局）</h3>
      <p><strong>触发条件</strong>：直接击败图尔格，不做任何额外收集。</p>
      <p>在这个结局中，你成为了新的洞窟之王。图尔格被消灭，哥布林军团群龙无首四散而逃。原本被哥布林占领的区域逐渐被其他种族收复。你坐在王座上，成为了地下世界新的守护者。这是一个中规中矩的结局，但你隐约感觉到，有些事情还没有结束……</p>

      <h3>结局二：觉醒者（真结局）</h3>
      <p><strong>触发条件</strong>：收集全部12块知识碎片，了解暗月之战的真相后击败图尔格。</p>
      <p>当你在遗忘图书馆中拼凑出完整的历史碎片后，你会发现图尔格并非天生邪恶。他是五大种族负面情绪的聚合体，是一个不该存在的悲剧造物。在最终战斗中，你选择<strong>净化而非消灭</strong>图尔格。暗月之力被驱散，五大种族的后裔从封印中苏醒，地下世界迎来了真正的复兴。</p>

      <h3>结局三：创世者（隐藏结局）</h3>
      <p><strong>触发条件</strong>：携带"原初之语"并满足以下全部条件——</p>
      <ul>
        <li>完成全部37个隐藏房间的探索</li>
        <li>与所有NPC好感度达到80以上</li>
        <li>不杀死任何可选的中立怪物（如石心巨人后裔）</li>
        <li>在图尔格战中触发隐藏对话并选择"我知道你的痛苦"</li>
      </ul>
      <p>隐藏结局将彻底改写地下世界的命运。你与图尔格达成和解，共同使用"原初之语"重写地下世界的规则。五大种族与哥布林族裔不再对立，一个全新的、和平的地下文明由此诞生。这个结局将解锁<strong>神话武器"原初之光"</strong>和<strong>New Game+模式</strong>。</p>

      <h3>结局选择的哲学</h3>
      <p>三个结局分别代表了面对"异类"时的三种态度——<strong>征服、理解与和解</strong>。哪个才是"正确"的结局？制作组没有给出答案。正如游戏中的一句台词所说："在黑暗的洞穴中，最重要的是你选择成为什么样的光。"</p>
    `,
  },
];